import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validators } from '@angular/forms';
import {
  ChangeDetectorRef,
  computed,
  Directive,
  inject,
  Injector,
  OnInit,
  runInInjectionContext,
  signal
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Fn } from '@coin/shared/core/util-models';
import { errorsToText } from './error-to-text.helper';
import { map, Observable, of, startWith, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

/** The bits of control state this base surfaces, normalized across the two control worlds. */
interface ControlState {
  errors: ValidationErrors | null;
  touched: boolean;
}

const emptyControlState: ControlState = { errors: null, touched: false };

/**
 * True when the control is AbstractControl-backed (Reactive Forms, or template-driven `ngModel`),
 * whose state is read from its event/status streams. False means a Signal Forms `[formField]`
 * interop control, whose state is exposed through signal-backed getters instead.
 */
function isAbstractControl(control: unknown): control is AbstractControl {
  return control instanceof AbstractControl;
}

/**
 * Base for the shared V2 form controls. Bridges to both worlds a control can be bound in:
 * Reactive Forms (an `AbstractControl`, whose state is read from its `events` stream) and Signal
 * Forms (an interop `NgControl` whose getters are signal-backed). `controlState$` collapses both
 * into one `{ errors, touched }` stream so everything below derives from a single source.
 */
@Directive() // decorator only to enable lifecycle compatibility
export abstract class DefaultControlValueAccessor<T> implements ControlValueAccessor, OnInit {
  protected cd = inject(ChangeDetectorRef);
  protected injector = inject(Injector);
  protected translateService = inject(TranslateService);

  protected ngControl = signal<NgControl | null>(null);

  private controlState$: Observable<ControlState> = toObservable(this.ngControl).pipe(
    switchMap(ngControl => (ngControl ? this.readControlState(ngControl) : of(emptyControlState)))
  );

  public errorTooltip = toSignal(this.controlState$.pipe(map(state => this.errorsToTooltip(state.errors))), {
    initialValue: null as string | null
  });
  public touched = toSignal(this.controlState$.pipe(map(state => state.touched)), { initialValue: false });

  private _disabled = signal(false);
  public disabled = this._disabled.asReadonly();

  public ngOnInit(): void {
    this.ngControl.set(this.injector.get(NgControl, null));
  }

  protected get isRequired(): boolean {
    const control = this.ngControl()?.control;
    if (!control) return false;
    // AbstractControl-backed: run the validator against an empty control.
    if (isAbstractControl(control)) {
      return !!control.validator?.({} as AbstractControl)?.required;
    }
    // Signal Forms interop: hasValidator(required) maps directly to the field's required() state.
    return !!(control as Pick<AbstractControl, 'hasValidator'>).hasValidator?.(Validators.required);
  }

  private readControlState(ngControl: NgControl): Observable<ControlState> {
    const read = (): ControlState => ({ errors: ngControl.errors, touched: !!ngControl.touched });
    const { control } = ngControl;

    return isAbstractControl(control)
      ? // AbstractControl-backed: re-read on every control event.
        control.events.pipe(startWith(null), map(read))
      : // Signal Forms Interop: getters are signals.
        runInInjectionContext(this.injector, () => toObservable(computed(read)));
  }

  private errorsToTooltip(errors: ValidationErrors | null): string | null {
    if (!errors) return null;
    return errorsToText(errors)
      .map(error => this.translateService.instant(error))
      .join('\n\n');
  }

  // for manual (via ui) value changes
  protected _value: T;
  get value(): T {
    return this._value;
  }
  set value(value: T) {
    this._value = value;
    this.onChange(value);
  }

  // for programmatic value changes
  writeValue(value: T) {
    this._value = value;
    this.cd.markForCheck();
  }

  onChange: Fn = () => {};
  registerOnChange(fn: Fn) {
    this.onChange = fn;
  }

  onTouch: Fn = () => {};
  registerOnTouched(fn: Fn) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this._disabled.set(isDisabled);
  }
}
